import { Button } from "@components/ui/Button"
import { Input } from "@components/ui/Input"
import { Message } from "@components/ui/Message"
import { NonIdealState } from "@components/ui/NonIdealState"
import { useFerve, useFerveSession } from "@features/ferve/FerveProvider"
import { useMutation } from "@tanstack/react-query"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { useFlyingCarpet } from "../useFlyingCarpet"
import { trackEvent } from "@features/tracking/tracking"

const SCREENS = {
  login: LoginScreen,
  dashboard: DashboardScreen,
}

export function AccountPanel() {
  const session = useFerveSession()

  const [screen, setScreen] = useState<keyof typeof SCREENS>(session?.loggedIn ? "dashboard" : "login")

  useEffect(() => {
    if (session?.loggedIn) {
      setScreen("dashboard")
    } else {
      setScreen("login")
    }
  }, [session?.loggedIn])

  const Component = SCREENS[screen]

  return (
    <div className="absolute inset-0" data-mixpanel-area="Account Panel">
      <AnimatePresence>
        <motion.div
          key={screen}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="absolute inset-0"
        >
          <Component />
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

function DashboardScreen() {
  const session = useFerveSession()
  const ferve = useFerve()
  const carpet = useFlyingCarpet()

  return (
    <div className="p-[--padding] flex flex-col justify-center gap-3 w-full h-full">
      {session?.firstName && <div className="text-center type-label-caps mb-3">Welcome back, {session.firstName}</div>}
      <div className="flex flex-col gap-3">
        <Button href={ferve.url("/Account/Bookings")} shape="branded" target="_blank">
          Bookings
        </Button>
        <Button href={ferve.url("/Account/Tickets")} shape="branded" target="_blank">
          Tickets
        </Button>
        <Button onClick={() => carpet.launch("planner")} shape="branded">
          My Watchlist
        </Button>
        <Button href={ferve.url("/Account/Edit")} shape="branded" target="_blank">
          Edit My Details
        </Button>
        <Button href={ferve.url("/Account/Main")} shape="branded" target="_blank">
          Manage Account
        </Button>
        <Button onClick={() => ferve.logout()} shape="branded" variant="primary">
          Logout
        </Button>
      </div>
    </div>
  )
}

function LoginScreen() {
  const ref = useRef<HTMLFormElement>(null)
  const ferve = useFerve()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")

  const login = useMutation({
    mutationKey: ["login"],
    mutationFn(vars: { username: string; password: string }) {
      return ferve.login(vars.username, vars.password)
    },
    onSuccess: (data) => {
      trackEvent(
        "Login",
        {
          Success: true,
        },
        ref.current,
      )
    },
    onError: (error) => {
      trackEvent(
        "Login",
        {
          Success: false,
        },
        ref.current,
      )
    },
  })

  return (
    <form
      className="p-[--padding] flex flex-col md:justify-center w-full h-full"
      ref={ref}
      onSubmit={(e) => {
        e.preventDefault()
        login.mutate({
          username: username,
          password: password,
        })
      }}
    >
      <NonIdealState
        icon="account"
        title="Sign into your account"
        description={
          <span>
            New to SFF?{" "}
            <a
              href={ferve.url("/Account/Signup")}
              className="text-fg-secondary basic-link"
              target="_blank"
              onClick={() =>
                trackEvent("Create Account Button", {}, ref.current, {
                  send_immediately: true,
                  transport: "sendBeacon",
                })
              }
            >
              Create an account here
            </a>
          </span>
        }
      />

      <div className="flex flex-col gap-4 mt-6">
        <Message variant="error">{login.error?.message}</Message>
        <Input
          type="email"
          autoComplete="email"
          placeholder="Email address"
          value={username}
          required
          onValueChange={setUsername}
          disabled={login.isPending}
          enterKeyHint="next"
          iosFocusFix
        />
        <Input
          type="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          required
          onValueChange={setPassword}
          disabled={login.isPending}
          enterKeyHint="done"
          iosFocusFix
        />
        <div className="flex justify-between items-center">
          <div className="type-body-s">
            <a
              href={ferve.url("/Account/Reset")}
              className="text-fg-secondary basic-link"
              onClick={() =>
                trackEvent("Forgot Password Button", {}, ref.current, {
                  send_immediately: true,
                  transport: "sendBeacon",
                })
              }
            >
              Forgot password
            </a>
          </div>
          <Button type="submit" shape="branded" variant="hero" loading={login.isPending}>
            Sign In
          </Button>
        </div>
      </div>
    </form>
  )
}
